// WALLET-P1 (scope additions 2026-10-27): the price modal's two lists — per-unit price lines
// (video analysed per minute, web search per call, an operator's own unit) and the markups of
// reported spend per category. Each row has visible Edit and Remove; Add opens a new row in
// place. Keys are lowercase (`stock_photo`), each once.
import {
  Button,
  FieldError,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@companyos/ui";
import { CheckIcon, PencilIcon, PlusIcon, Trash2Icon, XIcon } from "lucide-react";
import { useState } from "react";
import {
  bpText,
  parsePercent,
  parseRate,
  rateText,
  rateToInput,
} from "../../billing/billing-money";
import { DENSE_TABLE, RowAction, RowActions } from "../../settings/row-actions";

export type UnitPrice = { unit: string; label: string; per: string; price_micros: number };
export type CategoryMarkup = { category: string; markup_bp: number };

const KEY = /^[a-z][a-z0-9_]{1,39}$/;

type UnitDraft = { unit: string; label: string; per: string; price: string };

/** Why a unit draft cannot be saved, or null. */
function unitProblem(d: UnitDraft, taken: readonly string[]): string | null {
  if (!KEY.test(d.unit)) return "Key: lowercase letters, digits and _ (2–40).";
  if (taken.includes(d.unit)) return "That unit is listed already.";
  if (!d.label.trim() || d.label.trim().length > 60) return "Label: 1 to 60 characters.";
  if (!d.per.trim() || d.per.trim().length > 30) return "Per: 1 to 30 characters.";
  const p = parseRate(d.price);
  if (p === null || p > 1_000_000_000) return "Price: an amount like 0.005.";
  return null;
}

export function UnitPricesEditor({
  value,
  onChange,
  onEditing,
}: {
  value: readonly UnitPrice[];
  onChange: (next: UnitPrice[]) => void;
  /** Whether a row is open (the form waits until it is saved or cancelled). */
  onEditing: (editing: boolean) => void;
}) {
  // The row being edited: its index, or "new"; and its draft.
  const [editing, setEditing] = useState<number | "new" | null>(null);
  const [draft, setDraft] = useState<UnitDraft>({ unit: "", label: "", per: "", price: "" });
  const open = (at: number | "new") => {
    const u = at === "new" ? null : value[at];
    setDraft(
      u
        ? { unit: u.unit, label: u.label, per: u.per, price: rateToInput(u.price_micros) }
        : { unit: "", label: "", per: "", price: "" },
    );
    setEditing(at);
    onEditing(true);
  };
  const close = () => {
    setEditing(null);
    onEditing(false);
  };
  const taken = value.filter((_, i) => i !== editing).map((u) => u.unit);
  const problem = editing === null ? null : unitProblem(draft, taken);
  const save = () => {
    if (problem || editing === null) return;
    const line = {
      unit: draft.unit,
      label: draft.label.trim(),
      per: draft.per.trim(),
      price_micros: parseRate(draft.price) ?? 0,
    };
    onChange(
      editing === "new" ? [...value, line] : value.map((u, i) => (i === editing ? line : u)),
    );
    close();
  };
  const editRow = (key: string) => (
    <TableRow key={key} data-unit-edit className="align-top">
      <TableCell>
        <Input
          aria-label="Unit key"
          value={draft.unit}
          disabled={editing !== "new"}
          placeholder="stock_photo"
          onChange={(e) => setDraft({ ...draft, unit: e.target.value.trim().toLowerCase() })}
        />
      </TableCell>
      <TableCell>
        <Input
          aria-label="Unit label"
          value={draft.label}
          maxLength={60}
          placeholder="Stock photo"
          onChange={(e) => setDraft({ ...draft, label: e.target.value })}
        />
      </TableCell>
      <TableCell>
        <Input
          aria-label="Per"
          value={draft.per}
          maxLength={30}
          placeholder="photo"
          onChange={(e) => setDraft({ ...draft, per: e.target.value })}
        />
      </TableCell>
      <TableCell>
        <Input
          aria-label="Price ($)"
          inputMode="decimal"
          value={draft.price}
          placeholder="0.25"
          onChange={(e) => setDraft({ ...draft, price: e.target.value })}
        />
      </TableCell>
      <TableCell className="w-px">
        <span className="flex items-center gap-0.5">
          <RowAction
            icon={CheckIcon}
            label="Save unit"
            tip="Save"
            disabled={problem !== null}
            onClick={save}
          />
          <RowAction icon={XIcon} label="Cancel unit" tip="Cancel" onClick={close} />
        </span>
      </TableCell>
    </TableRow>
  );
  return (
    <div className="flex flex-col gap-2" data-slot="unit-prices">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium">Per-unit prices</span>
        <Button
          type="button"
          size="xs"
          variant="secondary"
          disabled={editing !== null}
          onClick={() => open("new")}
        >
          <PlusIcon data-icon="inline-start" aria-hidden="true" />
          Add unit
        </Button>
      </div>
      <Table className={DENSE_TABLE}>
        <TableHeader>
          <TableRow>
            <TableHead scope="col">Key</TableHead>
            <TableHead scope="col">Label</TableHead>
            <TableHead scope="col">Per</TableHead>
            <TableHead scope="col" className="text-right">
              Price
            </TableHead>
            <TableHead scope="col">
              <span className="sr-only">Actions</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {value.map((u, i) =>
            editing === i ? (
              editRow(u.unit)
            ) : (
              <TableRow key={u.unit} data-unit={u.unit} className="group/row">
                <TableCell className="font-mono text-xs text-muted-foreground">{u.unit}</TableCell>
                <TableHead scope="row" className="font-normal">
                  {u.label}
                </TableHead>
                <TableCell>{u.per}</TableCell>
                <TableCell className="text-right tabular-nums">
                  {rateText(u.price_micros)}
                </TableCell>
                <TableCell className="w-px text-right">
                  <RowActions>
                    <RowAction
                      icon={PencilIcon}
                      label={`Edit ${u.label}`}
                      tip="Edit"
                      disabled={editing !== null}
                      onClick={() => open(i)}
                    />
                    <RowAction
                      icon={Trash2Icon}
                      label={`Remove ${u.label}`}
                      tip="Remove"
                      disabled={editing !== null}
                      onClick={() => onChange(value.filter((_, j) => j !== i))}
                    />
                  </RowActions>
                </TableCell>
              </TableRow>
            ),
          )}
          {editing === "new" ? editRow("__new") : null}
          {value.length === 0 && editing === null ? (
            <TableRow>
              <TableCell colSpan={5} className="text-muted-foreground">
                No per-unit prices.
              </TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
      {problem && editing !== null ? <FieldError>{problem}</FieldError> : null}
    </div>
  );
}

export function CategoryMarkupsEditor({
  value,
  onChange,
  onEditing,
}: {
  value: readonly CategoryMarkup[];
  onChange: (next: CategoryMarkup[]) => void;
  onEditing: (editing: boolean) => void;
}) {
  const [editing, setEditing] = useState<number | "new" | null>(null);
  const [draft, setDraft] = useState({ category: "", pct: "" });
  const open = (at: number | "new") => {
    const c = at === "new" ? null : value[at];
    setDraft(
      c
        ? { category: c.category, pct: bpText(c.markup_bp).replace("%", "") }
        : { category: "", pct: "" },
    );
    setEditing(at);
    onEditing(true);
  };
  const close = () => {
    setEditing(null);
    onEditing(false);
  };
  const taken = value.filter((_, i) => i !== editing).map((c) => c.category);
  const bp = parsePercent(draft.pct);
  const problem =
    editing === null
      ? null
      : !KEY.test(draft.category)
        ? "Category: lowercase letters, digits and _ (2–40)."
        : taken.includes(draft.category)
          ? "That category is listed already."
          : bp === null || bp > 100_000
            ? "Markup: a percent from 0 to 1000."
            : null;
  const save = () => {
    if (problem || editing === null || bp === null) return;
    const line = { category: draft.category, markup_bp: bp };
    onChange(
      editing === "new" ? [...value, line] : value.map((c, i) => (i === editing ? line : c)),
    );
    close();
  };
  const editRow = (key: string) => (
    <TableRow key={key} data-category-edit>
      <TableCell>
        <Input
          aria-label="Category"
          value={draft.category}
          disabled={editing !== "new"}
          placeholder="stock_photo"
          onChange={(e) => setDraft({ ...draft, category: e.target.value.trim().toLowerCase() })}
        />
      </TableCell>
      <TableCell>
        <Input
          aria-label="Markup %"
          inputMode="decimal"
          value={draft.pct}
          placeholder="20"
          onChange={(e) => setDraft({ ...draft, pct: e.target.value })}
        />
      </TableCell>
      <TableCell className="w-px">
        <span className="flex items-center gap-0.5">
          <RowAction
            icon={CheckIcon}
            label="Save category"
            tip="Save"
            disabled={problem !== null}
            onClick={save}
          />
          <RowAction icon={XIcon} label="Cancel category" tip="Cancel" onClick={close} />
        </span>
      </TableCell>
    </TableRow>
  );
  return (
    <div className="flex flex-col gap-2" data-slot="category-markups">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium">Category markups</span>
        <Button
          type="button"
          size="xs"
          variant="secondary"
          disabled={editing !== null}
          onClick={() => open("new")}
        >
          <PlusIcon data-icon="inline-start" aria-hidden="true" />
          Add category
        </Button>
      </div>
      <Table className={DENSE_TABLE}>
        <TableHeader>
          <TableRow>
            <TableHead scope="col">Category</TableHead>
            <TableHead scope="col" className="text-right">
              Markup
            </TableHead>
            <TableHead scope="col">
              <span className="sr-only">Actions</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {value.map((c, i) =>
            editing === i ? (
              editRow(c.category)
            ) : (
              <TableRow key={c.category} data-category={c.category} className="group/row">
                <TableHead scope="row" className="font-mono text-xs font-normal">
                  {c.category}
                </TableHead>
                <TableCell className="text-right tabular-nums">{bpText(c.markup_bp)}</TableCell>
                <TableCell className="w-px text-right">
                  <RowActions>
                    <RowAction
                      icon={PencilIcon}
                      label={`Edit ${c.category}`}
                      tip="Edit"
                      disabled={editing !== null}
                      onClick={() => open(i)}
                    />
                    <RowAction
                      icon={Trash2Icon}
                      label={`Remove ${c.category}`}
                      tip="Remove"
                      disabled={editing !== null}
                      onClick={() => onChange(value.filter((_, j) => j !== i))}
                    />
                  </RowActions>
                </TableCell>
              </TableRow>
            ),
          )}
          {editing === "new" ? editRow("__new") : null}
          {value.length === 0 && editing === null ? (
            <TableRow>
              <TableCell colSpan={3} className="text-muted-foreground">
                None: reported spend uses the default markup.
              </TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
      {problem && editing !== null ? <FieldError>{problem}</FieldError> : null}
    </div>
  );
}
